import { Text } from '@m/alchemy-ui/Text';
import * as Styled from './Styled';
import { Button } from '@m/alchemy-ui/Button';
import {
  CoachingSettingsUpdateActionsEnum,
  useGetCoachingIntelligenceSettingsQuery,
  useGetCoachingIntelligenceTcQuery,
  useUpdateCoachingIntelligenceSettingsMutation,
} from 'graphql.types';
import { DateTime, fullMonthDateYearFormat } from 'common/utils/date';

function formatUtcDate(dateTime?: string | null) {
  return dateTime ? DateTime.fromISO(dateTime).toUTC().toFormat(fullMonthDateYearFormat) : '';
}

const ONE_ONE_ONES_URL = '/admin/company/one-on-ones';

const CoachingIntelligenceTnC = () => {
  const { data } = useGetCoachingIntelligenceSettingsQuery();
  const settings = data?.coachingIntelligenceSettings;
  const { data: dataTnC } = useGetCoachingIntelligenceTcQuery();
  const [updateSettings] = useUpdateCoachingIntelligenceSettingsMutation({
    onCompleted: data => {
      if (data.updateCoachingIntelligenceSettings.errors.length === 0) {
        window.location.href = ONE_ONE_ONES_URL;
      }
    },
  });
  const handleAccept = () => {
    updateSettings({
      variables: {
        action: CoachingSettingsUpdateActionsEnum.AcceptTerms,
      },
    });
  };
  const acceptedBy = settings?.termsAcceptedBy?.profile.fullName;
  const {
    termsAccepted,
    termsAcceptedAt,
    termsExpired,
    termsExpiry,
    termsExpiryAt,
    enabled: termsEnabled,
  } = settings || {};

  const acceptedDate = formatUtcDate(termsAcceptedAt);
  const expiryDate = formatUtcDate(termsExpiryAt);

  const termsRevisionMatches = settings?.termsRevisionMatches;

  let needsReapproval = false;
  if (termsRevisionMatches === false) {
    needsReapproval = true;
  }

  let acceptButtonText = 'I Agree - Enable Coaching Intelligence';
  let acceptButtonDisabled = termsAccepted && !needsReapproval;
  let notReadyButtonDisabled = termsAccepted && !needsReapproval;
  let notReadyButtonHref = termsAccepted && !needsReapproval ? undefined : ONE_ONE_ONES_URL;
  let footerMessage: string | null = null;

  if (termsExpiry && termsEnabled) {
    acceptButtonText = 'I Agree';
    acceptButtonDisabled = false;
    notReadyButtonDisabled = false;
    notReadyButtonHref = ONE_ONE_ONES_URL;
    if (acceptedBy && expiryDate) {
      footerMessage = `Use Policy accepted by ${acceptedBy} will expire on ${expiryDate}`;
    }
  } else if (termsExpired) {
    if (acceptedBy) {
      footerMessage = `Use policy accepted by ${acceptedBy} expired. Please review and accept the Acceptable Use Policy.`;
    }
  } else if (needsReapproval) {
    footerMessage = `Use Policy accepted by ${acceptedBy} on ${acceptedDate} has changed since the last approval. Please review and accept the latest version.`;
    acceptButtonText = 'I Agree - Approve Updated Policy';
    acceptButtonDisabled = false;
    notReadyButtonDisabled = false;
    notReadyButtonHref = ONE_ONE_ONES_URL;
  } else if (acceptedDate && acceptedBy) {
    footerMessage = `Use Policy accepted by ${acceptedBy} on ${acceptedDate}`;
  }

  return (
    <Styled.ConfigurationPage className="margin-top-large">
      <Styled.Background />
      <Styled.Container className="flex flex-column justify-center align-start">
        <a href={ONE_ONE_ONES_URL} className="flex align-center">
          <Styled.FixedArrowPreviousIcon aria-hidden="true" className="flex align-center" />
          <Text typeStyle="actionM" className="margin-left-tiny">
            Coaching
          </Text>
        </a>
        <Styled.Title>Coaching Intelligence Acceptable Use Policy</Styled.Title>
        <Styled.Section>
          <div>
            <Styled.TermsAndConditionsTextCSS
              className="coaching-terms-and-conditions"
              dangerouslySetInnerHTML={{
                __html: dataTnC?.coachingIntelligenceTc?.content || '',
              }}
            />
          </div>
          <div className="flex justify-center margin-top-largish">
            <Button onClick={handleAccept} disabled={acceptButtonDisabled}>
              {acceptButtonText}
            </Button>
            <Button
              className="margin-left-smallish"
              href={notReadyButtonHref}
              priority="tertiary"
              disabled={notReadyButtonDisabled}
            >
              I&apos;m Not Ready Yet
            </Button>
          </div>
          <br />
          {footerMessage && (
            <Text typeStyle="captionM" color="typeMeta" className="text-center">
              {footerMessage}
            </Text>
          )}
        </Styled.Section>
      </Styled.Container>
    </Styled.ConfigurationPage>
  );
};

export default CoachingIntelligenceTnC;
